import Cookies from 'js-cookie';

import { env, isDevelopment } from './env';

type ClientRouteLike = {
  pathname: string;
  search?: string;
  hash?: string;
};

export const REDIRECT_AFTER_PARAM = 'redirect_after';

const SESSION_STORAGE_KEY = '@auth:redirect-after';
const PENDING_AUTH_LINK_SESSION_STORAGE_KEY = '@auth:redirect-after:auth-link';
const AUTH_COOKIE_DOMAIN = isDevelopment() ? undefined : '.ecustos.com.br';
const AUTH_FLOW_PATH_PATTERNS = [
  /^\/login$/,
  /^\/login\/with-password$/,
  /^\/login\/with-social-provider$/,
  /^\/organization$/,
  /^\/password$/,
  /^\/forgot$/,
  /^\/auth\/sienge-id\/callback$/,
  /^\/auth\/sienge-id\/with\/organization\/[^/]+\/[^/]+$/,
];

const getRedirectAfterCookieKey = (): string => {
  const appEnv = env('VITE_APP_ENV');

  if (appEnv === 'production') {
    return 'ecustos-redirect-after-prd';
  }

  if (appEnv === 'staging') {
    return 'ecustos-redirect-after-stg';
  }

  return 'ecustos-redirect-after-dev';
};

const getPendingAuthLinkRedirectAfterCookieKey = (): string => {
  const appEnv = env('VITE_APP_ENV');

  if (appEnv === 'production') {
    return 'ecustos-redirect-after-auth-link-prd';
  }

  if (appEnv === 'staging') {
    return 'ecustos-redirect-after-auth-link-stg';
  }

  return 'ecustos-redirect-after-auth-link-dev';
};

const getCookieOptions = (): Cookies.CookieAttributes => {
  return {
    path: '/',
    ...(AUTH_COOKIE_DOMAIN ? { domain: AUTH_COOKIE_DOMAIN } : {}),
  };
};

const decodeQueryValue = (value: string): string | null => {
  try {
    return decodeURIComponent(value.replace(/\+/g, '%20'));
  } catch {
    return null;
  }
};

const getRedirectAfterQueryState = (
  search: string,
): { found: boolean; value: string | null } => {
  const normalizedSearch = search.startsWith('?') ? search.slice(1) : search;

  if (!normalizedSearch) {
    return { found: false, value: null };
  }

  const queryParts = normalizedSearch.split('&');

  for (const queryPart of queryParts) {
    if (queryPart) {
      const separatorIndex = queryPart.indexOf('=');
      const rawKey =
        separatorIndex >= 0 ? queryPart.slice(0, separatorIndex) : queryPart;
      const rawValue =
        separatorIndex >= 0 ? queryPart.slice(separatorIndex + 1) : '';

      const decodedKey = decodeQueryValue(rawKey);

      if (decodedKey === REDIRECT_AFTER_PARAM) {
        const decodedValue = decodeQueryValue(rawValue);

        return {
          found: true,
          value: validateRedirectTarget(decodedValue),
        };
      }
    }
  }

  return { found: false, value: null };
};

const getSessionStorage = (): Storage | null => {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
};

const writeStoredRedirectTarget = (target: string): void => {
  getSessionStorage()?.setItem(SESSION_STORAGE_KEY, target);
  Cookies.set(getRedirectAfterCookieKey(), target, getCookieOptions());
};

const writePendingAuthLinkRedirectTarget = (target: string): void => {
  getSessionStorage()?.setItem(PENDING_AUTH_LINK_SESSION_STORAGE_KEY, target);
  Cookies.set(
    getPendingAuthLinkRedirectAfterCookieKey(),
    target,
    getCookieOptions(),
  );
};

const isDefaultRedirectTarget = (target: string): boolean => target === '/';
const isUrlLikePath = (target: string): boolean => {
  return /^\/[a-z][a-z0-9+.-]*:\/\//i.test(target);
};

export const buildClientRouteTarget = ({
  pathname,
  search = '',
  hash = '',
}: ClientRouteLike): string => {
  return `${pathname || '/'}${search}${hash}`;
};

export const isAuthFlowPath = (pathname: string): boolean => {
  return AUTH_FLOW_PATH_PATTERNS.some((pattern) => pattern.test(pathname));
};

export const validateRedirectTarget = (
  value?: string | null,
): string | null => {
  if (!value) {
    return null;
  }

  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    value.includes('\\') ||
    isUrlLikePath(value)
  ) {
    return null;
  }

  try {
    const redirectUrl = new URL(value, window.location.origin);

    if (redirectUrl.origin !== window.location.origin) {
      return null;
    }

    if (!redirectUrl.pathname.startsWith('/')) {
      return null;
    }

    return `${redirectUrl.pathname}${redirectUrl.search}${redirectUrl.hash}`;
  } catch {
    return null;
  }
};

export const readRedirectTargetFromSearch = (search: string): string | null => {
  return getRedirectAfterQueryState(search).value;
};

export const persistRedirectTarget = (
  target?: string | null,
): string | null => {
  const validatedTarget = validateRedirectTarget(target);

  if (!validatedTarget) {
    return null;
  }

  if (isDefaultRedirectTarget(validatedTarget)) {
    clearRedirectTarget();
    return null;
  }

  writeStoredRedirectTarget(validatedTarget);

  return validatedTarget;
};

export const syncRedirectTargetFromSearch = (search: string): string | null => {
  const { found, value } = getRedirectAfterQueryState(search);

  if (!found) {
    return null;
  }

  if (!value) {
    clearRedirectTarget();
    return null;
  }

  return persistRedirectTarget(value);
};

export const clearRedirectTarget = (): void => {
  getSessionStorage()?.removeItem(SESSION_STORAGE_KEY);
  Cookies.remove(getRedirectAfterCookieKey(), getCookieOptions());
};

export const clearPendingAuthLinkRedirectTarget = (): void => {
  getSessionStorage()?.removeItem(PENDING_AUTH_LINK_SESSION_STORAGE_KEY);
  Cookies.remove(
    getPendingAuthLinkRedirectAfterCookieKey(),
    getCookieOptions(),
  );
};

export const getStoredRedirectTarget = (): string | null => {
  const sessionTarget = validateRedirectTarget(
    getSessionStorage()?.getItem(SESSION_STORAGE_KEY),
  );

  if (sessionTarget && !isDefaultRedirectTarget(sessionTarget)) {
    return sessionTarget;
  }

  const cookieTarget = validateRedirectTarget(
    Cookies.get(getRedirectAfterCookieKey()),
  );

  if (cookieTarget && !isDefaultRedirectTarget(cookieTarget)) {
    writeStoredRedirectTarget(cookieTarget);
    return cookieTarget;
  }

  clearRedirectTarget();

  return null;
};

export const persistPendingAuthLinkRedirectTarget = (
  target?: string | null,
): string | null => {
  const validatedTarget = validateRedirectTarget(target);

  if (!validatedTarget) {
    clearPendingAuthLinkRedirectTarget();
    return null;
  }

  if (isDefaultRedirectTarget(validatedTarget)) {
    clearPendingAuthLinkRedirectTarget();
    return null;
  }

  writePendingAuthLinkRedirectTarget(validatedTarget);

  return validatedTarget;
};

export const backupRedirectTargetFromSearch = (
  search: string,
): string | null => {
  return persistPendingAuthLinkRedirectTarget(
    readRedirectTargetFromSearch(search),
  );
};

export const restorePendingAuthLinkRedirectTarget = (): string | null => {
  const sessionTarget = validateRedirectTarget(
    getSessionStorage()?.getItem(PENDING_AUTH_LINK_SESSION_STORAGE_KEY),
  );

  if (sessionTarget && !isDefaultRedirectTarget(sessionTarget)) {
    clearPendingAuthLinkRedirectTarget();
    return persistRedirectTarget(sessionTarget);
  }

  const cookieTarget = validateRedirectTarget(
    Cookies.get(getPendingAuthLinkRedirectAfterCookieKey()),
  );

  clearPendingAuthLinkRedirectTarget();

  if (cookieTarget && !isDefaultRedirectTarget(cookieTarget)) {
    return persistRedirectTarget(cookieTarget);
  }

  return null;
};

export const getRedirectTarget = (search = ''): string | null => {
  const { found, value } = getRedirectAfterQueryState(search);

  if (found) {
    return value;
  }

  return getStoredRedirectTarget();
};

export const consumeRedirectTarget = (fallback = '/'): string => {
  const redirectTarget = getStoredRedirectTarget();

  clearRedirectTarget();

  return redirectTarget ?? fallback;
};

export const buildAuthFlowPath = (
  pathname: string,
  redirectTarget?: string | null,
): string => {
  const targetUrl = new URL(pathname, window.location.origin);
  const validatedRedirectTarget = validateRedirectTarget(redirectTarget);

  if (
    validatedRedirectTarget &&
    !isDefaultRedirectTarget(validatedRedirectTarget)
  ) {
    targetUrl.searchParams.set(REDIRECT_AFTER_PARAM, validatedRedirectTarget);
  } else {
    targetUrl.searchParams.delete(REDIRECT_AFTER_PARAM);
  }

  return `${targetUrl.pathname}${targetUrl.search}${targetUrl.hash}`;
};

export const buildLoginRedirectPath = (location: ClientRouteLike): string => {
  return buildAuthFlowPath('/login', buildClientRouteTarget(location));
};
